import { useEffect, useMemo, useRef, useContext } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useSnapshot } from 'valtio';
import { useSet } from '../../../../use/data/useSet';
import { firestore } from '../../../../services/startFirestore';
import { MessageContext } from '../../../../providers/MessageContext';
import type { DocListNamedView, DocListViewSettingsId, DocListViewSettingsInput } from '#types';
import { HOME_PAGE_SAVED_VIEWS_INITIAL_LIMIT } from '#types/System.Parameters.types';
import { createCollectionStore, type CollectionStore } from '../../../../store';

type HomePageSavedViewDoc = {
  docId?: string;
  name?: string;
  viewSettingsId?: DocListViewSettingsId;
  settings?: DocListViewSettingsInput;
  meta?: Record<string, unknown>;
};

interface UseHomePageSavedViewsProps {
  userId: string | undefined;
  viewSettingsId: DocListViewSettingsId;
}

const inactiveStore = createCollectionStore<HomePageSavedViewDoc>('users/__inactive__/homePageViews');

export const useHomePageSavedViews = ({ userId, viewSettingsId }: UseHomePageSavedViewsProps) => {
  const { messageApi } = useContext(MessageContext);
  const { batchSet } = useSet();

  const viewsStore = useMemo<CollectionStore<HomePageSavedViewDoc>>(
    () => userId
      ? createCollectionStore<HomePageSavedViewDoc>(`users/${userId}/homePageViews`, {
          queryOptions: {
            orderBy: { field: 'meta.lastModified', direction: 'desc' },
            extraConstraints: [{ type: 'limit', n: HOME_PAGE_SAVED_VIEWS_INITIAL_LIMIT }],
          },
        })
      : inactiveStore,
    [userId],
  );

  useEffect(() => {
    if (!userId) return;
    viewsStore.connect();
    return () => viewsStore.disconnect();
  }, [userId, viewsStore]);

  const snap = useSnapshot(viewsStore.state);
  const homePageViewsData = useMemo<HomePageSavedViewDoc[]>(() => {
    if (!userId) return [];
    const result: HomePageSavedViewDoc[] = [];
    for (const id of snap.docIds) {
      const entry = snap.docs.get(id);
      if (entry) result.push({ ...(entry.data as HomePageSavedViewDoc), docId: id });
    }
    return result;
  }, [userId, snap.docIds, snap.docs]);
  const homePageViewsLoading = userId ? snap.loading : false;

  const customViews: DocListNamedView[] = useMemo(() => {
    return homePageViewsData
      .filter((view) => view.viewSettingsId === viewSettingsId)
      .flatMap((view) => {
        if (!view.docId || typeof view.name !== 'string' || !view.settings) {
          return [];
        }
        return [{
          id: view.docId,
          name: view.name,
          settings: view.settings,
          meta: {
            createdAt: (view.meta as any)?.created,
            lastModified: (view.meta as any)?.lastModified,
            userId: (view.meta as any)?.userId,
          },
        }];
      });
  }, [homePageViewsData, viewSettingsId]);

  const legacyMigrationAttemptedRef = useRef(false);

  useEffect(() => {
    if (!userId) return;
    if (legacyMigrationAttemptedRef.current) return;
    if (homePageViewsLoading) return;

    const hasAnyCurrentViews = homePageViewsData.some((v) => v.viewSettingsId === viewSettingsId);
    if (hasAnyCurrentViews) {
      legacyMigrationAttemptedRef.current = true;
      return;
    }

    legacyMigrationAttemptedRef.current = true;

    void (async () => {
      try {
        const userSnap = await getDoc(doc(firestore, 'users', userId));
        const legacyBundle = (userSnap.data() as any)?.settings?.docListViews?.[viewSettingsId];
        const legacyViews = Array.isArray(legacyBundle?.views) ? legacyBundle.views : [];

        const operations = legacyViews.flatMap((view: any) => {
          const viewId = view?.id;
          const name = view?.name;
          const settings = view?.settings;

          if (typeof viewId !== 'string' || typeof name !== 'string' || !settings) {
            return [];
          }

          return [{
            path: `users/${userId}/homePageViews`,
            docId: viewId,
            data: {
              viewSettingsId,
              name,
              settings,
            },
          }];
        });

        if (operations.length === 0) {
          return;
        }

        await batchSet(operations);
        messageApi.success('Migrated saved views');
      } catch (e) {
        console.error('Failed to migrate legacy saved views', e);
      }
    })();
  }, [batchSet, homePageViewsData, homePageViewsLoading, messageApi, userId, viewSettingsId]);

  return {
    customViews,
    isLoading: homePageViewsLoading,
  };
};


